// RecipeListPage.tsx — the catalogue, loaded from the recipe API.
// Anyone may browse; signed-in users can add their own and filter to just
// theirs. Chrome (navbar, theme toggle, Container) comes from the Shell.
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { BsSearch, BsFunnel, BsXLg, BsJournalText, BsPlusLg } from 'react-icons/bs'
import Text from 'common/ui/Text'
import Dropdown from 'common/ui/Dropdown'
import RecipeCard from '../components/RecipeCard'
import { listRecipes, totalMass, type Recipe } from '../api'
import { useAuth } from '../hooks/useAuth'

const difficulties = ['all', 'beginner', 'intermediate', 'advanced', 'expert']

const RecipeListPage = () => {
    const navigate = useNavigate()
    const auth = useAuth()

    const [recipes, setRecipes] = useState<Recipe[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState('all')
    const [difficulty, setDifficulty] = useState('all')
    const [mineOnly, setMineOnly] = useState(false)

    const load = useCallback(() => {
        setLoading(true)
        listRecipes(mineOnly)
            .then(setRecipes)
            .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load recipes'))
            .finally(() => setLoading(false))
    }, [mineOnly])

    useEffect(load, [load])

    const categories = ['all', ...Array.from(new Set(recipes.map(r => r.category).filter(Boolean)))]

    const filtered = recipes.filter(r => {
        const q = search.toLowerCase()
        const matchSearch = r.title.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q)
        const matchCategory = category === 'all' || r.category === category
        const matchDifficulty = difficulty === 'all' || r.difficulty === difficulty
        return matchSearch && matchCategory && matchDifficulty
    })

    const filtersActive = search !== '' || category !== 'all' || difficulty !== 'all'
    const catalogueEmpty = !loading && recipes.length === 0

    const clearFilters = () => {
        setSearch('')
        setCategory('all')
        setDifficulty('all')
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                <div>
                    <h1 className="fs-3">Recipes</h1>
                    <p className="text-body-secondary mb-2">
                        Precisely engineered recipes. All ingredients measured by mass.
                        Every step numbered and timed.
                    </p>
                    {!catalogueEmpty && !loading && (
                        <Badge bg="secondary">
                            {recipes.length} {recipes.length === 1 ? 'recipe' : 'recipes'}
                        </Badge>
                    )}
                </div>
                {auth.authenticated && (
                    <Button variant="primary" onClick={() => navigate('/new')}
                        className="d-flex align-items-center gap-2">
                        <BsPlusLg /> New Recipe
                    </Button>
                )}
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading recipes…</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    {!catalogueEmpty && (
                        <Card className="mb-4">
                            <Card.Header className="d-flex align-items-center gap-2">
                                <BsFunnel /> Filters
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3 align-items-center">
                                    <Col md={6}>
                                        <Form.Label htmlFor="recipe-search" className="small text-body-secondary mb-1">
                                            <BsSearch className="me-1" /> Search
                                        </Form.Label>
                                        <Text id="recipe-search" type="search" placeholder="Search recipes…"
                                            value={search} onChange={e => setSearch(e.target.value)}
                                            style={{ width: '100%' }} />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label htmlFor="category-filter" className="small text-body-secondary mb-1">
                                            Category
                                        </Form.Label>
                                        <Dropdown id="category-filter" value={category} style={{ width: '100%' }}
                                            onChange={e => setCategory(e.target.value)}
                                            options={categories.map(c => ({
                                                value: c, label: c === 'all' ? 'All categories' : c,
                                            }))} />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label htmlFor="difficulty-filter" className="small text-body-secondary mb-1">
                                            Difficulty
                                        </Form.Label>
                                        <Dropdown id="difficulty-filter" value={difficulty} style={{ width: '100%' }}
                                            onChange={e => setDifficulty(e.target.value)}
                                            options={difficulties.map(d => ({
                                                value: d, label: d === 'all' ? 'All difficulties' : d,
                                            }))} />
                                    </Col>
                                </Row>
                                {auth.authenticated && (
                                    <Form.Check
                                        type="switch"
                                        id="mine-only"
                                        className="mt-3"
                                        label="Only my recipes"
                                        checked={mineOnly}
                                        onChange={e => setMineOnly(e.target.checked)}
                                    />
                                )}
                            </Card.Body>
                            <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
                                {filtersActive ? (
                                    <Button variant="link" size="sm"
                                        className="p-0 d-flex align-items-center gap-1" onClick={clearFilters}>
                                        <BsXLg /> Reset Filters
                                    </Button>
                                ) : <span />}
                                <span className="small text-body-secondary">
                                    {filtered.length} of {recipes.length} shown
                                </span>
                            </Card.Footer>
                        </Card>
                    )}

                    {catalogueEmpty ? (
                        <Card>
                            <Card.Header className="d-flex align-items-center gap-2">
                                <BsJournalText /> {mineOnly ? 'You Have No Recipes Yet' : 'No Recipes Yet'}
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">
                                    {mineOnly
                                        ? 'You have not written any recipes yet.'
                                        : 'The catalogue is empty. Every recipe here is written by the person who cooked it.'}
                                </p>
                            </Card.Body>
                            <Card.Footer className="d-flex justify-content-end">
                                {auth.authenticated ? (
                                    <Button variant="primary" size="sm" onClick={() => navigate('/new')}
                                        className="d-flex align-items-center gap-2">
                                        <BsPlusLg /> Add the First One
                                    </Button>
                                ) : (
                                    <span className="small text-body-secondary">Sign in to add one.</span>
                                )}
                            </Card.Footer>
                        </Card>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-5 text-body-secondary">
                            <p className="mb-1">No recipes match the current filters.</p>
                            <p className="small mb-0">Widen the search or reset the filters above.</p>
                        </div>
                    ) : (
                        <Row className="g-3">
                            {filtered.map(recipe => (
                                <Col key={recipe.id} md={6} lg={4}>
                                    <RecipeCard
                                        id={recipe.id}
                                        title={recipe.title}
                                        subtitle={recipe.subtitle}
                                        difficulty={recipe.difficulty}
                                        totalMassG={totalMass(recipe)}
                                        totalTimeMin={recipe.totalTimeMin}
                                        category={recipe.category}
                                        stepCount={recipe.steps.length}
                                        canEdit={recipe.canEdit}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </>
            )}

            <footer className="mt-5 pt-4 border-top text-center text-body-secondary small">
                <p className="mb-0">Cooking — Technical Recipe Documentation</p>
            </footer>
        </>
    )
}

export default RecipeListPage
